<script lang="ts">
  import { Modal, Button, FormSelect, FormInput, FormCheckbox } from '../common';

  type TransactionGroup = {
    description: string;
    count: number;
    transactionIds: string[];
  };

  type Suggestion = {
    category: string;
    confidence: number;
    reasoning: string;
    displayName: string;
  };

  type Props = {
    isOpen: boolean;
    currentGroup: TransactionGroup;
    currentGroupIndex: number;
    totalGroups: number;
    currentSuggestion: Suggestion | null;
    categorizingCurrent: boolean;
    createRuleForCurrent: boolean;
    categories: string[];
    onClose: () => void;
    onCategorize: () => void;
    onAccept: () => void;
    onSkip: () => void;
    onSuggestionChange: (field: 'category' | 'displayName', value: string) => void;
    onCreateRuleChange: (value: boolean) => void;
  };

  let {
    isOpen,
    currentGroup,
    currentGroupIndex,
    totalGroups,
    currentSuggestion,
    categorizingCurrent,
    createRuleForCurrent,
    categories,
    onClose,
    onCategorize,
    onAccept,
    onSkip,
    onSuggestionChange,
    onCreateRuleChange
  }: Props = $props();
</script>

<Modal
  {isOpen}
  onClose={onClose}
  title="Categorize Transactions"
  subtitle="Transaction group {currentGroupIndex + 1} of {totalGroups}"
  size="md"
>
  {#snippet children()}
    <div class="transaction-info">
      <div class="description-label">Transaction Description:</div>
      <div class="description-value">{currentGroup.description}</div>
      <div class="count-badge">{currentGroup.count} transaction{currentGroup.count > 1 ? 's' : ''}</div>
    </div>

    {#if !currentSuggestion}
      <div class="wizard-actions">
        <Button
          variant="primary"
          onclick={onCategorize}
          disabled={categorizingCurrent}
        >
          {categorizingCurrent ? 'Asking AI...' : 'Categorize with AI'}
        </Button>
        <Button
          variant="secondary"
          onclick={onSkip}
        >
          Skip
        </Button>
      </div>
    {:else}
      <div class="suggestion-card">
        <div class="suggestion-header">
          <div class="confidence-badge" class:high={currentSuggestion.confidence >= 0.8} class:medium={currentSuggestion.confidence >= 0.5 && currentSuggestion.confidence < 0.8} class:low={currentSuggestion.confidence < 0.5}>
            {Math.round(currentSuggestion.confidence * 100)}% confidence
          </div>
        </div>
        {#if currentSuggestion.reasoning}
          <div class="suggestion-reasoning">{currentSuggestion.reasoning}</div>
        {/if}

        <div class="suggestion-fields">
          <FormSelect
            label="Category"
            value={currentSuggestion.category}
            options={categories}
            onchange={(e) => onSuggestionChange('category', e.target.value)}
          />
          <FormInput
            label="Display Name"
            value={currentSuggestion.displayName}
            onchange={(e) => onSuggestionChange('displayName', e.target.value)}
            placeholder="Enter a clean merchant name"
          />
        </div>

        <div class="rule-option">
          <FormCheckbox
            label="Create rule for future transactions with this description"
            checked={createRuleForCurrent}
            onchange={(e) => onCreateRuleChange(e.target.checked)}
          />
        </div>

        <div class="wizard-actions">
          <Button
            variant="primary"
            onclick={onAccept}
          >
            Accept & Continue
          </Button>
          <Button
            variant="secondary"
            onclick={onSkip}
          >
            Skip
          </Button>
        </div>
      </div>
    {/if}
  {/snippet}
</Modal>

<style lang="scss">
  .transaction-info {
    margin-bottom: var(--spacing-xl);
    padding: var(--spacing-lg);
    background: var(--color-background-secondary);
    border-radius: var(--border-radius-md);

    .description-label {
      font-size: var(--font-size-xs);
      color: var(--color-text-tertiary);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin-bottom: var(--spacing-xs);
    }

    .description-value {
      font-size: var(--font-size-lg);
      font-weight: var(--font-weight-medium);
      color: var(--color-text-primary);
      margin-bottom: var(--spacing-sm);
    }

    .count-badge {
      display: inline-block;
      padding: var(--spacing-xs) var(--spacing-sm);
      background: var(--color-primary);
      color: white;
      border-radius: var(--border-radius-sm);
      font-size: var(--font-size-xs);
      font-weight: var(--font-weight-medium);
    }
  }

  .suggestion-card {
    margin-top: var(--spacing-xl);
    padding: var(--spacing-lg);
    background: var(--color-background-secondary);
    border-radius: var(--border-radius-md);
    border: var(--border-width-medium) solid var(--color-primary);

    .suggestion-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: var(--spacing-md);
      gap: var(--spacing-md);
    }

    .confidence-badge {
      padding: var(--spacing-xs) var(--spacing-sm);
      border-radius: var(--border-radius-sm);
      font-size: var(--font-size-xs);
      font-weight: var(--font-weight-medium);
      white-space: nowrap;

      &.high {
        background: var(--color-success-background, var(--color-success-light));
        color: var(--color-success, var(--color-success));
      }

      &.medium {
        background: var(--color-warning-light);
        color: var(--color-warning, var(--color-warning));
      }

      &.low {
        background: var(--color-error-background, var(--color-danger-light));
        color: var(--color-error, var(--color-danger-hover));
      }
    }

    .suggestion-reasoning {
      font-size: var(--font-size-sm);
      color: var(--color-text-secondary);
      line-height: var(--line-height-relaxed);
      margin-bottom: var(--spacing-md);
      font-style: italic;
    }

    .suggestion-fields {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-md);
      margin-top: var(--spacing-lg);
      margin-bottom: var(--spacing-lg);
    }

    .rule-option {
      margin: var(--spacing-lg) 0;
      padding-top: var(--spacing-md);
      border-top: var(--border-width-thin) solid var(--color-border);
    }
  }

  .wizard-actions {
    display: flex;
    gap: var(--spacing-md);
    margin-top: var(--spacing-lg);
  }
</style>
